// Custom tabs spécifiques par CLI (phase 2).
//
// 4 factories retournant chacune un CliCustomTab, branchées additivement dans
// cli-config.ts. Tous consomment des tools DÉJÀ implémentés côté C# (aucune
// modification backend) :
//   - cli_native_sessions_list { cli, workspace? } → { sessions: NativeSessionSummary[] }
//   - cli_native_session_read  { cli, sessionId }  → { events: CliEvent[] }
//   - cli_steer                { sessionId, text } → { ok: true }
//   - cli_send_message         { sessionId, approveToolUseId } → approbation
//
// Sources d'inspiration :
//   - /Volumes/ssd-portable/agent-os-2/extras/aight-analysis/17-abstraction-design.md §2.4 (PlansProvider)
//   - 14-KIMI-analysis.md (approvals), 13-HERMES-analysis.md (sessions natives)
import { Show, For, createSignal, createMemo, createEffect, type JSX } from "solid-js";
import { Collapsible } from "@/shared/collapsible";
import { aggregate, extractLastTodoWrite } from "./aggregate";
import { fmtDateTime, prettyJson } from "./format";
import type { CliCustomTab, CliCustomTabContext } from "./cli-config";
import type { CliEvent, NativeSessionSummary, TodoWriteItem } from "./types";

// ─────────────────────────────────────────────────────────────────────────────
// Tab PLAN (Claude) — extrait le dernier TodoWrite de la session native active.
// Source : agent-os-2/17-abstraction-design.md §2.4 PlansProvider (JsonlTodoWrite).
// La logique pure extractLastTodoWrite() vit dans aggregate.ts (testable sans UI).
// ─────────────────────────────────────────────────────────────────────────────

function statusIcon(status: TodoWriteItem["status"]): string {
  switch (status) {
    case "completed":
      return "✓";
    case "in_progress":
      return "►";
    case "pending":
      return "○";
  }
}

export function makePlanTab(cli: string): CliCustomTab {
  return {
    id: "plan",
    label: "Plan",
    render: (ctx: CliCustomTabContext): JSX.Element => <PlanView cli={cli} ctx={ctx} />,
  };
}

function PlanView(props: { cli: string; ctx: CliCustomTabContext }): JSX.Element {
  // client = objet stable (non réactif) ; session = accessor réactif. Capturés
  // en const pour la lisibilité — cf. eslint-disable pattern views.tsx:256.
  // eslint-disable-next-line solid/reactivity
  const client = props.ctx.client;
  // eslint-disable-next-line solid/reactivity
  const session = props.ctx.session;
  const [todos, setTodos] = createSignal<TodoWriteItem[] | null>(null);
  const [loading, setLoading] = createSignal(false);
  const [err, setErr] = createSignal("");
  const [nativeSessions, setNativeSessions] = createSignal<NativeSessionSummary[]>([]);
  const [selectedNativeId, setSelectedNativeId] = createSignal<string | null>(null);

  const targetNativeId = createMemo(() => {
    // Priorité : session live avec nativeSessionId, sinon sélection manuelle.
    const live = session()?.nativeSessionId;
    if (live) return live;
    return selectedNativeId();
  });

  async function loadPlan() {
    const sid = targetNativeId();
    if (!sid) {
      setErr("Aucune session native. Démarrez une session ou sélectionnez-en une ci-dessous.");
      setTodos(null);
      return;
    }
    setLoading(true);
    setErr("");
    try {
      const r = (await client.call("cli_native_session_read", { cli: props.cli, sessionId: sid })) as {
        events?: CliEvent[];
      };
      setTodos(extractLastTodoWrite(r.events || []));
      if (!extractLastTodoWrite(r.events || [])) {
        setErr("Aucun TodoWrite trouvé dans cette session (le plan apparaîtra quand Claude l'utilisera).");
      }
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }

  async function loadNativeSessions() {
    try {
      const r = (await client.call("cli_native_sessions_list", { cli: props.cli })) as {
        sessions?: NativeSessionSummary[];
      };
      setNativeSessions(r.sessions || []);
    } catch {
      // silencieux : la sonde native peut échouer si le CLI n'est pas installé
    }
  }

  // Recharge le plan quand la cible change (effet, pas valeur dérivée).
  createEffect(() => {
    const sid = targetNativeId();
    if (sid) void loadPlan();
  });

  const completed = createMemo(() => todos()?.filter((t) => t.status === "completed").length ?? 0);
  const total = createMemo(() => todos()?.length ?? 0);

  return (
    <div class="cli-plan">
      <div class="cli-toolbar">
        <button
          class="btn btn--sm btn--ghost"
          onClick={() => void loadPlan()}
          disabled={loading() || !targetNativeId()}
        >
          {loading() ? "Chargement…" : "Actualiser le plan"}
        </button>
        <Show when={total() > 0}>
          <span class="cli-plan-progress" title="Todos complétés / total">
            {completed()} / {total()}
          </span>
        </Show>
        <Show when={targetNativeId()}>
          <span class="cli-plan-source" title="Session native lue">
            📄 {targetNativeId()?.slice(0, 12)}…
          </span>
        </Show>
      </div>

      <Show when={err()}>
        <div class="panel-error">{err()}</div>
      </Show>

      <Show
        when={todos() && todos()!.length > 0}
        fallback={
          <div class="panel-empty">
            Le plan vivant (TodoWrite) apparaîtra ici dès que Claude l'écrira. Lancez une session et demandez une tâche
            structurée.
          </div>
        }
      >
        <div class="cli-plan-list">
          <For each={todos()}>
            {(t) => (
              <div class="cli-plan-row" data-status={t.status}>
                <span class="cli-plan-icon" data-status={t.status}>
                  {statusIcon(t.status)}
                </span>
                <span class="cli-plan-content">{t.content}</span>
                <Show when={t.activeForm && t.status === "in_progress"}>
                  <span class="cli-plan-active">{t.activeForm}…</span>
                </Show>
              </div>
            )}
          </For>
        </div>
      </Show>

      {/* Sélecteur de session native si la session live n'a pas de nativeSessionId. */}
      <Show when={!session()?.nativeSessionId}>
        <Collapsible
          variant="default"
          class="cli-plan-natives"
          header={<span>📋 Sessions natives ({nativeSessions().length})</span>}
        >
          <div class="cli-toolbar">
            <button class="btn btn--xs btn--ghost" onClick={() => void loadNativeSessions()}>
              Lister
            </button>
          </div>
          <Show when={nativeSessions().length > 0}>
            <div class="cli-plan-natives-list">
              <For each={nativeSessions().slice(0, 20)}>
                {(s) => (
                  <div
                    class="cli-plan-native-row"
                    data-active={selectedNativeId() === s.sessionId || undefined}
                    onClick={() => {
                      setSelectedNativeId(s.sessionId);
                      void loadPlan();
                    }}
                  >
                    <span class="cli-plan-native-title">{s.title || s.sessionId.slice(0, 12)}</span>
                    <Show when={s.updatedAt}>
                      <span class="cli-plan-native-date">{fmtDateTime(s.updatedAt)}</span>
                    </Show>
                  </div>
                )}
              </For>
            </div>
          </Show>
        </Collapsible>
      </Show>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Tab STEER (Codex) — réorientation mid-flight via cli_steer.
// ─────────────────────────────────────────────────────────────────────────────

export function makeSteerTab(): CliCustomTab {
  return {
    id: "steer",
    label: "Steer",
    render: (ctx: CliCustomTabContext): JSX.Element => <SteerView ctx={ctx} />,
  };
}

interface SteerRecord {
  at: string;
  text: string;
  ok: boolean;
  err?: string;
}

function SteerView(props: { ctx: CliCustomTabContext }): JSX.Element {
  // eslint-disable-next-line solid/reactivity
  const { client, session } = props.ctx;
  const [draft, setDraft] = createSignal("");
  const [sending, setSending] = createSignal(false);
  const [err, setErr] = createSignal("");
  const [history, setHistory] = createSignal<SteerRecord[]>([]);

  async function steer() {
    const text = draft().trim();
    const sid = session()?.sessionId;
    if (!text || !sid || sending()) return;
    setSending(true);
    setErr("");
    try {
      await client.call("cli_steer", { sessionId: sid, text });
      setHistory((prev) => [{ at: new Date().toISOString(), text, ok: true }, ...prev]);
      setDraft("");
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e);
      setErr(msg);
      setHistory((prev) => [{ at: new Date().toISOString(), text, ok: false, err: msg }, ...prev]);
    } finally {
      setSending(false);
    }
  }

  return (
    <div class="cli-steer">
      <Show
        when={session()}
        fallback={
          <div class="panel-empty">Démarrez une session Codex pour utiliser le steer (réorientation mid-flight).</div>
        }
      >
        <div class="cli-steer-input-row">
          <textarea
            class="cli-steer-input"
            value={draft()}
            rows={3}
            placeholder="Instruction de réorientation (steer) — injectée pendant que Codex réfléchit…"
            disabled={session()?.status === "exited"}
            onInput={(e) => setDraft(e.currentTarget.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                void steer();
              }
            }}
          />
          <button
            class="btn btn--sm btn--primary"
            onClick={() => void steer()}
            disabled={sending() || !draft().trim() || session()?.status === "exited"}
            title="Réorienter (Cmd/Ctrl+Entrée)"
          >
            {sending() ? "Envoi…" : "Réorienter"}
          </button>
        </div>

        <Show when={err()}>
          <div class="panel-error">{err()}</div>
        </Show>

        <Show when={history().length > 0}>
          <div class="cli-steer-history">
            <div class="cli-steer-history-title">Historique des steers</div>
            <For each={history()}>
              {(h) => (
                <div class="cli-steer-record" data-ok={h.ok || undefined}>
                  <span class="cli-steer-record-status">{h.ok ? "✓" : "⚠"}</span>
                  <span class="cli-steer-record-text">{h.text}</span>
                  <span class="cli-steer-record-time">{fmtDateTime(h.at)}</span>
                  <Show when={h.err}>
                    <span class="cli-steer-record-err">{h.err}</span>
                  </Show>
                </div>
              )}
            </For>
          </div>
        </Show>
      </Show>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Tab APPROBATIONS (Kimi / générique) — liste les tool_use pendingApproval.
// Réutilise aggregate() + toolUseNeedsApproval() du flux live.
// Source : 14-KIMI-analysis.md (permission.set_mode), 17-abstraction-design.md §2.5.
// ─────────────────────────────────────────────────────────────────────────────

export function makeApprovalsTab(): CliCustomTab {
  return {
    id: "approvals",
    label: "Approbations",
    render: (ctx: CliCustomTabContext): JSX.Element => <ApprovalsView ctx={ctx} />,
  };
}

function ApprovalsView(props: { ctx: CliCustomTabContext }): JSX.Element {
  // eslint-disable-next-line solid/reactivity
  const { client, stream, session } = props.ctx;

  const blocks = createMemo(() => aggregate(stream.events()));
  // Tools en attente d'approbation (needsApproval && pas encore de résultat).
  const pending = createMemo(
    () =>
      blocks().filter(
        (b) =>
          b.kind === "tool" &&
          (b as { needsApproval: boolean }).needsApproval &&
          b.kind === "tool" &&
          (b as { toolResult: unknown }).toolResult === null,
      ) as Array<{
        id: string;
        toolName: string;
        toolInput: unknown;
        nativeId: string | null;
        needsApproval: boolean;
        toolResult: unknown | null;
        at: string;
      }>,
  );
  // Tools déjà approuvés (avec résultat).
  const resolved = createMemo(
    () =>
      blocks().filter(
        (b) =>
          b.kind === "tool" &&
          (b as { needsApproval: boolean }).needsApproval &&
          (b as { toolResult: unknown }).toolResult !== null,
      ) as Array<{
        id: string;
        toolName: string;
        toolInput: unknown;
        nativeId: string | null;
        toolResult: unknown | null;
        at: string;
      }>,
  );

  async function approve(nativeId: string | null) {
    const sid = session()?.sessionId;
    if (!sid || !nativeId) return;
    try {
      await client.call(
        "cli_send_message",
        { sessionId: sid, approveToolUseId: nativeId },
        { userConfirmedAt: new Date().toISOString() },
      );
    } catch {
      // l'erreur sera visible via le stream (event error)
    }
  }

  return (
    <div class="cli-approvals">
      <Show
        when={session()}
        fallback={<div class="panel-empty">Démarrez une session pour voir les demandes d'approbation d'outils.</div>}
      >
        <Show
          when={pending().length > 0}
          fallback={
            <div class="panel-empty">
              Aucune approbation en attente. Les outils nécessitant validation apparaîtront ici.
            </div>
          }
        >
          <div class="cli-approvals-pending">
            <div class="cli-approvals-title">En attente ({pending().length})</div>
            <For each={pending()}>
              {(b) => (
                <Collapsible
                  variant="tool"
                  class="cli-approvals-block"
                  defaultOpen={true}
                  header={
                    <span class="cli-approvals-head">
                      <span class="cli-approvals-name">⏳ {b.toolName}</span>
                      <button
                        class="btn btn--xs btn--primary"
                        onClick={(e) => {
                          e.stopPropagation();
                          void approve(b.nativeId);
                        }}
                      >
                        Approuver
                      </button>
                    </span>
                  }
                >
                  <pre class="cli-json">{prettyJson(b.toolInput)}</pre>
                </Collapsible>
              )}
            </For>
          </div>
        </Show>

        <Show when={resolved().length > 0}>
          <Collapsible
            variant="default"
            class="cli-approvals-resolved"
            header={<span>✓ Approuvés ({resolved().length})</span>}
          >
            <For each={resolved()}>
              {(b) => (
                <div class="cli-approvals-resolved-row">
                  <span class="cli-approvals-name">✓ {b.toolName}</span>
                  <span class="cli-approvals-time">{fmtDateTime(b.at)}</span>
                </div>
              )}
            </For>
          </Collapsible>
        </Show>
      </Show>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Tab SESSIONS NATIVES (générique) — historique natif via cli_native_sessions_list.
// Fonctionne pour claude/codex/kimi/gemini (readers natifs implémentés).
// ─────────────────────────────────────────────────────────────────────────────

export function makeNativeSessionsTab(cli: string): CliCustomTab {
  return {
    id: "natives",
    label: "Sessions natives",
    render: (ctx: CliCustomTabContext): JSX.Element => <NativeSessionsView cli={cli} ctx={ctx} />,
  };
}

function NativeSessionsView(props: { cli: string; ctx: CliCustomTabContext }): JSX.Element {
  // eslint-disable-next-line solid/reactivity
  const { client } = props.ctx;
  const [sessions, setSessions] = createSignal<NativeSessionSummary[]>([]);
  const [loading, setLoading] = createSignal(false);
  const [err, setErr] = createSignal("");
  const [selectedId, setSelectedId] = createSignal<string | null>(null);
  const [events, setEvents] = createSignal<CliEvent[]>([]);
  const [loadingEvents, setLoadingEvents] = createSignal(false);

  async function refresh() {
    setLoading(true);
    setErr("");
    try {
      const r = (await client.call("cli_native_sessions_list", { cli: props.cli })) as {
        sessions?: NativeSessionSummary[];
      };
      setSessions(r.sessions || []);
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }

  // Chargement initial (une fois au montage).
  createEffect(() => {
    void refresh();
  });

  async function openSession(sid: string) {
    setSelectedId(sid);
    setLoadingEvents(true);
    try {
      const r = (await client.call("cli_native_session_read", { cli: props.cli, sessionId: sid })) as {
        events?: CliEvent[];
      };
      setEvents(r.events || []);
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setLoadingEvents(false);
    }
  }

  const visibleEvents = createMemo(() => events().filter((e) => e.kind !== "raw" && e.kind !== "exit"));

  return (
    <div class="cli-natives">
      <div class="cli-toolbar">
        <button class="btn btn--sm btn--ghost" onClick={() => void refresh()} disabled={loading()}>
          {loading() ? "Chargement…" : "Actualiser"}
        </button>
        <span class="cli-natives-count">{sessions().length} session(s) native(s)</span>
      </div>

      <Show when={err()}>
        <div class="panel-error">{err()}</div>
      </Show>

      <div class="cli-natives-layout">
        <Show
          when={sessions().length > 0}
          fallback={<div class="panel-empty">Aucune session native trouvée pour ce CLI.</div>}
        >
          <div class="cli-natives-list">
            <For each={sessions().slice(0, 50)}>
              {(s) => (
                <div
                  class="cli-natives-row"
                  data-active={selectedId() === s.sessionId || undefined}
                  onClick={() => void openSession(s.sessionId)}
                >
                  <span class="cli-natives-title">{s.title || s.sessionId.slice(0, 12)}</span>
                  <Show when={s.cwd}>
                    <span class="cli-natives-cwd" title={s.cwd || ""}>
                      {s.cwd}
                    </span>
                  </Show>
                  <Show when={s.updatedAt}>
                    <span class="cli-natives-date">{fmtDateTime(s.updatedAt)}</span>
                  </Show>
                </div>
              )}
            </For>
          </div>
        </Show>

        <Show when={selectedId()}>
          <div class="cli-natives-detail">
            <div class="cli-natives-detail-head">
              <span class="cli-natives-detail-id">{selectedId()?.slice(0, 16)}…</span>
              <Show when={loadingEvents()}>
                <span class="cli-natives-detail-loading">Lecture…</span>
              </Show>
            </div>
            <Show
              when={visibleEvents().length > 0}
              fallback={<div class="panel-empty">Session vide ou illisible.</div>}
            >
              <div class="cli-natives-events">
                <For each={visibleEvents()}>
                  {(ev) => (
                    <div class="cli-natives-event" data-kind={ev.kind}>
                      <Show when={ev.kind === "user_msg" || ev.kind === "assistant_msg"}>
                        <span class="cli-natives-event-role">
                          {ev.role || (ev.kind === "user_msg" ? "user" : "assistant")}
                        </span>
                        <span class="cli-natives-event-content">{ev.content}</span>
                      </Show>
                      <Show when={ev.kind === "thinking"}>
                        <span class="cli-natives-event-role">💭 thinking</span>
                        <span class="cli-natives-event-content cli-natives-event-thinking">{ev.content}</span>
                      </Show>
                      <Show when={ev.kind === "tool_use"}>
                        <span class="cli-natives-event-role">🔧 {ev.toolName}</span>
                        <pre class="cli-json cli-natives-event-tool">{prettyJson(ev.toolInput)}</pre>
                      </Show>
                      <Show when={ev.kind === "error" || ev.kind === "status"}>
                        <span class="cli-natives-event-content" data-kind={ev.kind}>
                          {ev.content}
                        </span>
                      </Show>
                    </div>
                  )}
                </For>
              </div>
            </Show>
          </div>
        </Show>
      </div>
    </div>
  );
}
